// VRP Constraints Modal Component - Parameter Configuration

import React, { useState, useEffect } from 'react';
import { Popup } from 'devextreme-react/popup';
import { Button } from 'devextreme-react/button';
import { NumberBox } from 'devextreme-react/number-box';
import { CheckBox } from 'devextreme-react/check-box';
import { TextBox } from 'devextreme-react/text-box';

export interface VRPConstraints {
  // Kısıtlamaları Kaldır
  ignoreConstraints?: boolean;         // Tüm kısıtlamaları devre dışı bırak

  // Çalışma Saatleri
  startTime?: string;                  // Başlangıç saati (HH:MM)
  endTime?: string;                    // Bitiş saati (HH:MM)
  serviceDuration?: number;            // Servis süresi (saniye)

  // Öğle Molası
  lunchBreak?: boolean;                // Öğle molası aktif mi
  lunchBreakStart?: string;            // Öğle molası başlangıç (HH:MM)
  lunchBreakEnd?: string;              // Öğle molası bitiş (HH:MM)

  // 1. Zaman & Mesafe Kısıtlamaları
  maxWorkingHours?: number;            // Maksimum çalışma saati (saat), 0 = limitsiz
  maxDistance?: number;                // Maksimum mesafe (km), min 500, 0 = limitsiz

  // 2. Görev Kısıtlamaları (Task Constraints)
  maxTasks?: number | null;            // Maksimum müşteri sayısı (null = sınırsız)

  // 3. Mola Kısıtlamaları (Break Constraints)
  enableBreaks?: boolean;              // Constraint-based break'leri aktif eder
  breakDuration?: number;              // Mola süresi (dakika)
  breakAfterHours?: number;            // Kaç saat sonra mola

  // 4. Öncelik Kısıtlamaları (Priority Constraints)
  enablePriority?: boolean;            // Müşteri öncelik sistemini aktif eder
  priorityWeight?: number;             // Öncelik ağırlık faktörü (1-10)

  // 5. Yetenek/Beceri Kısıtlamaları (Skills Constraints)
  enableSkills?: boolean;              // Yetenek/beceri sistemini aktif eder

  // 6. Hız Kısıtlamaları (Speed Constraints)
  speedFactor?: number;                // Hız faktörü (0.5-1.5 arası, 1.0 = normal)

  // 7. Yol Kısıtlamaları (Route Preferences)
  avoidTolls?: boolean;                // Ücretli yollardan kaçın
  avoidHighways?: boolean;             // Otoyollardan kaçın
  avoidTunnels?: boolean;              // Tünellerden kaçın
  avoidFerries?: boolean;              // Feribot yollarından kaçın

  // 8. Optimizasyon Hedefi
  optimizationGoal?: 'balanced' | 'time' | 'distance' | 'vehicles';  // Optimizasyon hedefi
}

interface VRPConstraintsModalProps {
  visible: boolean;
  onHide: () => void;
  onSave: (constraints: VRPConstraints) => void;
  initialConstraints?: VRPConstraints;
  mode?: 'edit' | 'confirm'; // 'edit' = normal edit mode, 'confirm' = confirmation before route calculation
  onConfirm?: () => void; // Called when user confirms (only in 'confirm' mode)
}

export const VRPConstraintsModal: React.FC<VRPConstraintsModalProps> = ({
  visible,
  onHide,
  onSave,
  initialConstraints = {},
  mode = 'edit',
  onConfirm
}) => {
  const defaultConstraints: VRPConstraints = {
    // Kısıtlamaları Kaldır
    ignoreConstraints: false,

    // Çalışma Saatleri
    startTime: '08:00',
    endTime: '18:00',
    serviceDuration: 2400,

    // Öğle Molası
    lunchBreak: true,
    lunchBreakStart: '12:30',
    lunchBreakEnd: '13:30',

    // 1. Zaman & Mesafe Kısıtlamaları
    maxWorkingHours: 0,      // 0 = limitsiz
    maxDistance: 0,          // 0 = limitsiz, min 500 km

    // 2. Görev Kısıtlamaları
    maxTasks: null,

    // 3. Mola Kısıtlamaları
    enableBreaks: true,
    breakDuration: 30,
    breakAfterHours: 4,

    // 4. Öncelik Kısıtlamaları
    enablePriority: false,
    priorityWeight: 5,

    // 5. Yetenek/Beceri Kısıtlamaları
    enableSkills: false,

    // 6. Hız Kısıtlamaları
    speedFactor: 1.0,

    // 7. Yol Kısıtlamaları
    avoidTolls: false,
    avoidHighways: false,
    avoidTunnels: false,
    avoidFerries: false,

    // 8. Optimizasyon Hedefi
    optimizationGoal: 'balanced'
  };

  const [constraints, setConstraints] = useState<VRPConstraints>({
    ...defaultConstraints,
    ...initialConstraints
  });

  // Update local state when initialConstraints changes
  useEffect(() => {
    setConstraints({
      ...defaultConstraints,
      ...initialConstraints
    });
  }, [visible]);

  const handleSave = () => {
    onSave(constraints);
    onHide();
  };

  const handleReset = () => {
    setConstraints(defaultConstraints);
  };

  return (
    <Popup
      visible={visible}
      onHiding={onHide}
      dragEnabled={true}
      closeOnOutsideClick={false}
      showTitle={false}
      width={900}
      height="90vh"
    >
      {/* Başlık Bar */}
      <div style={{
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        height: '50px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 16px',
        borderBottom: '1px solid #e2e8f0',
        background: '#ffffff',
        zIndex: 1000
      }}>
        {/* Sol - X Butonu ve Başlık */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Button
            icon="close"
            onClick={onHide}
            stylingMode="text"
            type="normal"
          />
          <h2 style={{
            margin: 0,
            fontSize: '16px',
            fontWeight: '600',
            color: '#1e293b'
          }}>
            {mode === 'confirm' ? 'Rota Kısıtlamalarını Onayla ve Hesapla' : 'Rota Optimizasyon Kısıtlamaları'}
          </h2>
        </div>

        {/* Sağ - Butonlar */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {mode === 'edit' && (
            <Button
              text="Sıfırla"
              onClick={handleReset}
              stylingMode="text"
              type="normal"
            />
          )}
          <Button
            text={mode === 'confirm' ? 'Onayla ve Hesapla' : 'Kaydet'}
            onClick={() => {
              handleSave();
              if (mode === 'confirm' && onConfirm) {
                onConfirm();
              }
            }}
            stylingMode="contained"
            type={mode === 'confirm' ? 'success' : 'default'}
          />
        </div>
      </div>

      <div style={{ padding: '20px', paddingTop: '70px', overflow: 'auto', height: '100%' }}>

        {/* Kısıtlamaları Kaldır Butonu - B2B Style */}
        <div style={{
          marginBottom: '24px',
          padding: '16px',
          background: '#f8fafc',
          borderRadius: '8px',
          border: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px'
        }}>
          <div style={{ flex: 1 }}>
            <h3 style={{
              margin: '0 0 4px 0',
              fontSize: '14px',
              fontWeight: '600',
              color: '#1e293b'
            }}>
              {constraints.ignoreConstraints ? 'Kısıtlamalar Devre Dışı' : 'Kısıtlamalar Aktif'}
            </h3>
            <p style={{
              margin: 0,
              fontSize: '12px',
              color: '#64748b'
            }}>
              {constraints.ignoreConstraints
                ? 'Rota hesaplaması kısıtlamasız yapılacak.'
                : 'Kısıtlamalar rota hesaplamasında uygulanacak.'}
            </p>
          </div>
          <Button
            text={constraints.ignoreConstraints ? 'Etkinleştir' : 'Kaldır'}
            onClick={() => setConstraints({ ...constraints, ignoreConstraints: !constraints.ignoreConstraints })}
            stylingMode="outlined"
            type="normal"
            style={{ minWidth: '100px' }}
          />
        </div>

        {/* Çalışma Saatleri & Öğle Molası */}
        <div style={{
          marginBottom: '20px',
          padding: '16px',
          background: '#ffffff',
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: '600', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Çalışma Saatleri & Molalar
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                Başlangıç Saati
              </label>
              <TextBox
                value={constraints.startTime}
                onValueChanged={(e) => setConstraints({ ...constraints, startTime: e.value })}
                stylingMode="outlined"
                inputAttr={{ type: 'time' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                Bitiş Saati
              </label>
              <TextBox
                value={constraints.endTime}
                onValueChanged={(e) => setConstraints({ ...constraints, endTime: e.value })}
                stylingMode="outlined"
                inputAttr={{ type: 'time' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                Servis Süresi (sn)
              </label>
              <NumberBox
                value={constraints.serviceDuration}
                onValueChanged={(e) => setConstraints({ ...constraints, serviceDuration: e.value })}
                min={0}
                step={300}
                showSpinButtons={true}
                stylingMode="outlined"
              />
            </div>
          </div>

          <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '16px' }}>
            <div style={{ marginBottom: '12px' }}>
              <CheckBox
                value={constraints.lunchBreak}
                onValueChanged={(e) => setConstraints({ ...constraints, lunchBreak: e.value })}
                text="Öğle Molası Aktif"
              />
            </div>

            {constraints.lunchBreak && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                    Mola Başlangıç
                  </label>
                  <TextBox
                    value={constraints.lunchBreakStart}
                    onValueChanged={(e) => setConstraints({ ...constraints, lunchBreakStart: e.value })}
                    stylingMode="outlined"
                    inputAttr={{ type: 'time' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                    Mola Bitiş
                  </label>
                  <TextBox
                    value={constraints.lunchBreakEnd}
                    onValueChanged={(e) => setConstraints({ ...constraints, lunchBreakEnd: e.value })}
                    stylingMode="outlined"
                    inputAttr={{ type: 'time' }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>

          {/* Zaman Kısıtlamaları */}
          <div style={{
            padding: '16px',
            background: '#ffffff',
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
          }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: '600', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Zaman & Mesafe Kısıtlamaları
            </h3>

            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                Maksimum Çalışma Süresi (saat)
              </label>
              <NumberBox
                value={constraints.maxWorkingHours}
                onValueChanged={(e) => setConstraints({ ...constraints, maxWorkingHours: e.value })}
                min={0}
                max={24}
                step={0.5}
                showSpinButtons={true}
                stylingMode="outlined"
                placeholder="0 = Limitsiz"
              />
              <small style={{ color: '#64748b', fontSize: '11px' }}>0 = limitsiz</small>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                Maksimum Mesafe (km)
              </label>
              <NumberBox
                value={constraints.maxDistance}
                onValueChanged={(e) => setConstraints({ ...constraints, maxDistance: e.value })}
                min={0}
                max={2000}
                step={50}
                showSpinButtons={true}
                stylingMode="outlined"
                placeholder="0 = Limitsiz"
              />
              <small style={{ color: '#64748b', fontSize: '11px' }}>0 = limitsiz, aktif için min 500 km</small>
            </div>
          </div>

          {/* Mola Kısıtlamaları */}
          <div style={{
            padding: '16px',
            background: '#ffffff',
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
          }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: '600', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Mola Kısıtlamaları
            </h3>

            <div style={{ marginBottom: '12px' }}>
              <CheckBox
                value={constraints.enableBreaks}
                onValueChanged={(e) => setConstraints({ ...constraints, enableBreaks: e.value })}
                text="Zorunlu Mola Aktif"
              />
            </div>

            {constraints.enableBreaks && (
              <>
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                    Mola Süresi (dakika)
                  </label>
                  <NumberBox
                    value={constraints.breakDuration}
                    onValueChanged={(e) => setConstraints({ ...constraints, breakDuration: e.value })}
                    min={15}
                    max={120}
                    step={15}
                    showSpinButtons={true}
                    stylingMode="outlined"
                  />
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                    Kaç Saat Sonra Mola
                  </label>
                  <NumberBox
                    value={constraints.breakAfterHours}
                    onValueChanged={(e) => setConstraints({ ...constraints, breakAfterHours: e.value })}
                    min={1}
                    max={8}
                    step={0.5}
                    showSpinButtons={true}
                    stylingMode="outlined"
                  />
                </div>
              </>
            )}
          </div>

          {/* Görev Kısıtlamaları */}
          <div style={{
            padding: '16px',
            background: '#ffffff',
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
          }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: '600', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Görev Kısıtlamaları
            </h3>

            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                Maksimum Müşteri Sayısı (Araç Başına) - Opsiyonel
              </label>
              <NumberBox
                value={constraints.maxTasks ?? undefined}
                onValueChanged={(e) => setConstraints({ ...constraints, maxTasks: e.value || null })}
                min={1}
                max={100}
                step={1}
                showSpinButtons={true}
                stylingMode="outlined"
                placeholder="Sınırsız"
              />
              <small style={{ color: '#64748b', fontSize: '11px' }}>Bir aracın ziyaret edebileceği max müşteri sayısı</small>
            </div>
          </div>

          {/* Müşteri Öncelik & Yetenek Kısıtlamaları */}
          <div style={{
            padding: '16px',
            background: '#ffffff',
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
          }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: '600', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Müşteri Kısıtlamaları
            </h3>

            <div style={{ marginBottom: '12px' }}>
              <CheckBox
                value={constraints.enablePriority}
                onValueChanged={(e) => setConstraints({ ...constraints, enablePriority: e.value })}
                text="Müşteri Önceliği Aktif"
              />
            </div>

            {constraints.enablePriority && (
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                  Öncelik Ağırlığı (1-10)
                </label>
                <NumberBox
                  value={constraints.priorityWeight}
                  onValueChanged={(e) => setConstraints({ ...constraints, priorityWeight: e.value })}
                  min={1}
                  max={10}
                  step={1}
                  showSpinButtons={true}
                  stylingMode="outlined"
                />
                <small style={{ color: '#64748b', fontSize: '11px' }}>Priority değeri bu faktörle çarpılır</small>
              </div>
            )}

            <div style={{ marginBottom: '12px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
              <CheckBox
                value={constraints.enableSkills}
                onValueChanged={(e) => setConstraints({ ...constraints, enableSkills: e.value })}
                text="Yetenek/Beceri Sistemi Aktif"
              />
              <small style={{ display: 'block', marginTop: '6px', color: '#64748b', fontSize: '11px' }}>
                Araçlar sadece gerekli yeteneklere sahip müşterileri ziyaret edebilir
              </small>
            </div>
          </div>

          {/* Hız Kısıtlamaları */}
          <div style={{
            padding: '16px',
            background: '#ffffff',
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
          }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: '600', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Hız Kısıtlamaları
            </h3>

            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                Hız Faktörü (0.5 - 1.5)
              </label>
              <NumberBox
                value={constraints.speedFactor}
                onValueChanged={(e) => setConstraints({ ...constraints, speedFactor: e.value })}
                min={0.5}
                max={1.5}
                step={0.1}
                format="#0.0"
                showSpinButtons={true}
                stylingMode="outlined"
              />
              <small style={{ color: '#64748b', fontSize: '11px' }}>
                1.0 = Normal, &lt;1.0 = Yavaş (trafik), &gt;1.0 = Hızlı (otoyol)
              </small>
            </div>
          </div>

          {/* Rota Tercihleri */}
          <div style={{
            padding: '16px',
            background: '#ffffff',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            gridColumn: '1 / -1'
          }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: '600', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Rota Tercihleri
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <CheckBox
                  value={constraints.avoidTolls}
                  onValueChanged={(e) => setConstraints({ ...constraints, avoidTolls: e.value })}
                  text="Ücretli Yollardan Kaçın"
                />
              </div>
              <div>
                <CheckBox
                  value={constraints.avoidHighways}
                  onValueChanged={(e) => setConstraints({ ...constraints, avoidHighways: e.value })}
                  text="Otoyollardan Kaçın"
                />
              </div>
              <div>
                <CheckBox
                  value={constraints.avoidTunnels}
                  onValueChanged={(e) => setConstraints({ ...constraints, avoidTunnels: e.value })}
                  text="Tünellerden Kaçın"
                />
              </div>
              <div>
                <CheckBox
                  value={constraints.avoidFerries}
                  onValueChanged={(e) => setConstraints({ ...constraints, avoidFerries: e.value })}
                  text="Feribot Yollarından Kaçın"
                />
              </div>
            </div>
          </div>

        </div>
      </div>
    </Popup>
  );
};
